import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import {_throw as observableThrowError } from 'rxjs/observable/throw';
import { catchError, switchMap, timeout } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';
import { Router } from '@angular/router'; // Router for navigation
import { JwtUtils } from '../../auth/utilities/jwtUtils';
import { LoggingService } from '../services/logging.service';
import { ConsoleLogger } from '../utilities/console-logger';
import { extractHttpErrorMessage } from '../utilities/http-error';
import { LocalStorageKeys } from '../constants/localStorageKeys.enum';
import { ToastrService } from 'ngx-toastr';
import { NO_TIMEOUT_HEADER } from '../constants/no-timeout-header';

@Injectable()
export class MainInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private accessTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private requestsQueue: Array<() => Observable<HttpEvent<any>>> = [];

  constructor(
    public authService: AuthService,
    private loggingService: LoggingService,
    private router: Router,
    private toastrService: ToastrService,
  ) {
    window.addEventListener('storage', this.syncTabs.bind(this));
    this.authService.isRefreshing$.subscribe(x => this.isRefreshing = x);
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // A request sent with NO_TIMEOUT_HEADER skips the 90 second timeout below (the header itself is not sent).
    const noTimeout = request.headers.has(NO_TIMEOUT_HEADER);

    request = request.clone({
      withCredentials: true,
      headers: noTimeout ? request.headers.delete(NO_TIMEOUT_HEADER) : request.headers
    });

    if (JwtUtils.accessToken) {
      request = this.injectBearerToken(request, JwtUtils.accessToken);
    }

    request = this.injectUserIdInHeader(request);

    const timeoutOperator: (source: Observable<HttpEvent<any>>) => Observable<HttpEvent<any>> = noTimeout
      ? (source: Observable<HttpEvent<any>>) => source
      : timeout<HttpEvent<any>>(90000);

    return next
      .handle(request)
      .pipe(
          timeoutOperator,
          catchError(errorResponse => {
            if (errorResponse && errorResponse.name === 'TimeoutError') {
              this.toastrService.error('Request timed out. Please try again.', 'Timeout');
              return Observable.throw(errorResponse);
            }

            const logDetail = {
              currentUrl: this.router.url,
              user: this.authService.currentUser,
              error: errorResponse
            };

            if (errorResponse instanceof HttpErrorResponse) {
              switch(errorResponse.status) {
                case 401:
                  if (JwtUtils.accessToken && !request.url.toLowerCase().includes('/auth/')) {
                    return this.handle401Error(request, next);
                  }

                  ConsoleLogger.logInfo('401 error on auth request...');
                  return Observable.throw(errorResponse);
                case 403:
                  this.toastrService.error("Not authorized to access this resource.  For more information, please contact IT.", "Not Authorized");
                  break;
                default:
                  this.loggingService.logEvent("AppLog - Error - ResponseInterceptor", { logDetail });

                  if (errorResponse.error && errorResponse.error.errors) {
                    for (const err in errorResponse.error.errors) {
                      if (!errorResponse.error.errors.hasOwnProperty(err)) { continue; }
                      ConsoleLogger.logInfo(err);
                      this.toastrService.error(`${errorResponse.error.errors[err]}`, "Error");
                    }
                  } else {
                    const message = extractHttpErrorMessage(errorResponse);
                    this.toastrService.error(message || `${errorResponse.error}`, "Error");
                  }
              }
            } else {
                this.loggingService.logEvent("AppLog - Error - RequestInterceptor", { logDetail });
                return Observable.throw(errorResponse);
              }
            return observableThrowError(errorResponse);
          })
      );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isRefreshing) {
      ConsoleLogger.logInfo('Already refreshing session...');
      return new Observable(observer => {
        this.requestsQueue.push(() => {
          ConsoleLogger.logInfo(`Queueing request ${request.url}...`);
          const newRequest = next.handle(this.injectBearerToken(request, this.accessTokenSubject.value));

          newRequest.subscribe(
            res => {
              observer.next(res);
              observer.complete();
            },
            err => {
              observer.error(err);
              observer.complete();
            });

          return newRequest;
        })
      });
    }

    ConsoleLogger.logInfo('Not currently refreshing token. Refreshing token now...');
    this.isRefreshing = true;
    this.accessTokenSubject.next(null);

    return this.authService.refreshToken()
      .pipe(
        switchMap((token: any) => {
          this.isRefreshing = false;
          this.accessTokenSubject.next(token.accessToken);
          localStorage.setItem(LocalStorageKeys.IS_REFRESHING_SESSION, 'false');

          this.requestsQueue.forEach((callback) => {
            ConsoleLogger.logInfo(`Executing queued request...`, callback);
            callback()
          });

          this.requestsQueue = [];
          return next.handle(this.injectBearerToken(request, token.accessToken));
        }),
        catchError((error) => {
          ConsoleLogger.logInfo('Error refreshing token: ', error);
          this.isRefreshing = false;
          this.authService.logout();
          this.router.navigate(['/auth/login']);
          return observableThrowError(error);
        })
      );
  }

  private syncTabs(event: StorageEvent) {
    if (event.key === 'isRefreshing' && event.newValue === 'true') {
      this.isRefreshing = true;
      this.authService.getNewToken()
        .subscribe(token => {
          this.accessTokenSubject.next(token);
        });
    }
  }

  private injectBearerToken(request: HttpRequest<any>, accessToken: string): HttpRequest<any> {
    return request.clone({ setHeaders: { 'Authorization': `Bearer ${accessToken}` }});
  }

  private injectUserIdInHeader(request: HttpRequest<any>) {
    if (this.authService.currentUser) {
        const userId = this.authService.currentUser.id.toString();

        if (userId) {
            request = request.clone({ setHeaders: { 'UserId': userId }});
        }
    }
    return request;
  }
}



// WEBPACK FOOTER //
// ./src/app/core/interceptors/main.interceptor.ts